import { pubSub } from '@utils/PubSub';

export default class SwymServices {
  constructor() {
    this.instance = null;
    this.state = {
      lists: [],
      isInitialized: false,
      isLoading: false,
      error: false,
    };
    this.config = {
      retryAttempt: 0,
      maxRetries: 10,
      delay: 1000,
      timeoutId: null,
      list: {
        lname: 'Wishlist',
      },
    };
  }
  get windowAvailability() {
    return window._swat && typeof window._swat === 'object';
  }
  get listContents() {
    return this.state.lists.map((list) => list.listcontents).flat();
  }
  get listId() {
    return this.state.lists.find((list) => list.lid)?.lid ?? false;
  }
  async init() {
    console.log('Initializing Swym Services...', this.state.isInitialized);
    if (this.state.isInitialized) return this.instance;

    this.instance = await this.mountSwym();
    return this.instance;
  }
  handleError(error) {
    this.state.error = true;
    const template = document.querySelector('body');
    template.classList.add('wishlist-hidden');
    throw new Error(`Swym Failed To Update ${error}`);
  }
  async waitForSwym() {
    if (this.config.retryAttempt >= this.config.maxRetries) {
      this.handleError();
      throw new Error('Swym service failed to load after maximum retries');
    }

    this.config.retryAttempt++;

    return new Promise((resolve, reject) => {
      this.config.timeoutId = setTimeout(async () => {
        try {
          if (this.windowAvailability) {
            await this.mountSwym();
            resolve();
          } else {
            console.log(
              `Swym not available, retrying... (${this.config.retryAttempt})`
            );
            resolve(this.waitForSwym());
          }
        } catch (error) {
          reject(error);
        }
      }, this.config.delay);
    });
  }
  async generateList() {
    if (!this.windowAvailability) {
      throw new Error('Swym is not available for use');
    }

    const { list } = this.config;
    return new Promise((resolve, reject) => {
      window._swat.createList(
        list,
        async (list) => {
          console.log('No Lists Found. New Swym List Created', list);
          await this.fetchLists();
          resolve();
        },
        (error) => {
          console.error('Failed to Create New Swym List', error);
          reject();
        }
      );
    });
  }
  async mountSwym() {
    if (this.state.isInitialized) return;
    console.log('Mounting Swym Services...');
    try {
      if (!this.windowAvailability) {
        await this.waitForSwym();
        return;
      }
      await this.fetchLists();
      this.state.isInitialized = true;
      pubSub.publish('swym:service:ready');
    } catch (error) {
      this.handleError();
    }
  }
  async fetchLists() {
    if (!this.windowAvailability) {
      throw new Error('Swym is not available for use');
    }
    this.state.isLoading = true;
    console.log('Swym is available, fetching lists...');
    return new Promise((resolve, reject) => {
      window._swat.fetchLists({
        callbackFn: async (lists) => {
          await this.onListsFetched(lists);
          resolve();
        },
        errorFn: () => {
          reject(new Error('Failed to fetch Swym lists'));
        },
      });
    });
  }
  async onListsFetched(lists) {
    this.state.isLoading = false;
    this.config.retryAttempt = 0;
    this.state.lists = lists || [];
    if (this.state.lists.length <= 0) {
      await this.generateList();
      return;
    }
    console.log('Swym lists fetched:', this.state.lists);
  }
  async isInWishlist(productId, selectedGalleryOption) {
    if (!productId || !this.state.isInitialized) return false;
    if (!productId || this.listContents.length === 0) return false;

    return this.listContents.find(({ empi, cprops }) => {
      return (
        empi === productId && cprops.selectedOption === selectedGalleryOption
      );
    });
  }
  async addToWishlist(product) {
    if (!this.windowAvailability || !product) {
      throw new Error('Swym is not available for use or invalid product data');
    }

    console.log('Swym Adding to List', product);
    return new Promise((resolve, reject) => {
      window._swat.addToList(
        this.listId,
        product,
        async (item) => {
          await this.fetchLists();
          resolve({ action: 'added', title: item.dt });
        },
        () => {
          this.handleError();
          reject();
        }
      );
    });
  }
  async removeFromWishlist(product) {
    if (!this.windowAvailability || !product) {
      throw new Error('Swym is not available for use or invalid product data');
    }
    console.log('Swym Removing from List', product);
    return new Promise((resolve, reject) => {
      window._swat.deleteFromList(
        this.listId,
        product,
        async (item) => {
          await this.fetchLists();
          resolve({ action: 'removed', title: item.dt });
        },
        () => {
          this.handleError();
          reject();
        }
      );
    });
  }
}

export const useSwym = new SwymServices();
